import React, { Fragment } from 'react'
import { makeStyles, Box, LinearProgress, Button } from '@material-ui/core'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import useTheme from '@material-ui/styles/useTheme'
import { ReactComponent as InfoIcon } from '../../images/icons/info.svg'
import { lighten } from '@material-ui/core/styles/colorManipulator'
import { useHistory } from 'react-router-dom'
import clsx from 'clsx'
import ArrowBackOutlinedIcon from 'components/material-symbol/icons/ArrowBack'

const useStyles = makeStyles(theme => ({
  barContainer: {
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.drawer + 1,

    display: 'flex',
    flexDirection: 'column',

    backgroundColor: lighten(theme.palette.info.light, 0.9),
  },
  barContainerPlain: {
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  barContainerPlainBottom: {
    borderBottom: 'none',
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
  barContainerToolbar: {
    backgroundColor: theme.palette.common.white,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  barContainerToolbarBottom: {
    backgroundColor: theme.palette.common.white,
    borderBottom: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  barContainerBottom: {
    position: 'sticky',
    top: 'auto',
    bottom: 0,
    right: 0,
    zIndex: '1000',
    width: '100%',
  },
  barContainerBottomFixed: {
    position: 'fixed',
    top: 'auto',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar,
    width: '100%',
  },
  error: {
    backgroundColor: lighten(theme.palette.error.light, 0.9),
  },
  bar: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '14px',
    paddingBottom: '14px',
    paddingRight: theme.spacing(5),
    paddingLeft: theme.spacing(5),
  },
  toolbarBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: theme.spacing(2, 4),
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2),
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2.5, 2),
    },
  },
  toolbarBarEnd: {
    justifyContent: 'flex-end',
  },
  cancelToolbar: {
    textTransform: 'none',
    fontWeight: 600,
    color: '#172024',
    paddingLeft: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.5),
      marginLeft: 0,
    },
  },
  toolbarSave: {
    '& .MuiButton-root': {
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: 24,
      padding: theme.spacing(1, 2.5),
      minWidth: 'auto',
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
      },
      '&.Mui-disabled': {
        boxShadow: 'none',
      },
    },
  },
  informationContainer: {
    flexGrow: '1',
    display: 'flex',
    alignItems: 'center',
  },
  infoIcon: {
    color: theme.palette.info.main,
    marginRight: '12px',
  },
  actionContainer: {
    display: 'flex',
  },
}))

export default function ActionBar({
  children,
  instructions,
  loading,
  error,
  includeCancel,
  disableCancel,
  onCancel,
  stickToBottom,
  cancelLabel = 'Cancel',
  plain = false,
  variant = 'default',
}) {
  const history = useHistory()
  const theme = useTheme()
  const classes = useStyles()
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'), {
    defaultMatches: false,
  })
  const handleCancel = onCancel || (() => history.goBack())
  const isToolbar = variant === 'toolbar'

  const resolvedStickToBottom =
    stickToBottom === true ||
    (stickToBottom !== false && isToolbar && isMobileViewport)
  const useFixedToolbarBottom =
    isToolbar && isMobileViewport && resolvedStickToBottom

  return (
    <Box
      className={clsx(classes.barContainer, {
        [classes.error]: error,
        [classes.barContainerBottom]:
          resolvedStickToBottom && !useFixedToolbarBottom,
        [classes.barContainerBottomFixed]: useFixedToolbarBottom,
        [classes.barContainerPlain]: plain && !error && !isToolbar,
        [classes.barContainerPlainBottom]:
          plain && !error && !isToolbar && resolvedStickToBottom,
        [classes.barContainerToolbar]:
          isToolbar && !error && !resolvedStickToBottom,
        [classes.barContainerToolbarBottom]:
          isToolbar && !error && resolvedStickToBottom,
      })}
    >
      {isToolbar ? (
        <Box
          className={clsx(classes.toolbarBar, {
            [classes.toolbarBarEnd]: !includeCancel,
          })}
        >
          {includeCancel ? (
            <Button
              type="button"
              className={classes.cancelToolbar}
              startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
              disabled={disableCancel}
              onClick={handleCancel}
            >
              {cancelLabel}
            </Button>
          ) : null}
          {children && <Box className={classes.toolbarSave}>{children}</Box>}
        </Box>
      ) : (
        <Box className={classes.bar}>
          <Box className={classes.informationContainer}>
            {instructions && (
              <Fragment>
                <InfoIcon className={classes.infoIcon} />
                {instructions}
              </Fragment>
            )}
          </Box>
          <Box className={classes.actionContainer}>
            {includeCancel && (
              <Button
                color="primary"
                disabled={disableCancel}
                onClick={handleCancel}
              >
                {cancelLabel}
              </Button>
            )}

            {children && <Box ml={2}>{children}</Box>}
          </Box>
        </Box>
      )}

      {loading && (
        <Box width="100%">
          <LinearProgress color="primary" />
        </Box>
      )}
    </Box>
  )
}
